import { useEffect, useState } from 'react';
import DrawerFlow, { type DrawerFlowStep } from 'src/UI/drawer-flow/DrawerFlow';
import { useExportBranding } from 'src/secret-menu/export-assets/hooks/useExportBranding';
import type { Company } from 'src/secret-menu/hooks/useCompanies';
import SelectTenantStep from 'src/secret-menu/auto-brand/steps/SelectTenantStep';
import UploadDocumentStep from 'src/secret-menu/auto-brand/steps/UploadDocumentStep';
import ManualCorrectionStep from 'src/secret-menu/auto-brand/steps/ManualCorrectionStep';
import TitlePageStep from 'src/secret-menu/auto-brand/steps/TitlePageStep';
import UploadLogosStep from 'src/secret-menu/auto-brand/steps/UploadLogosStep';
import ReviewSubmitStep from 'src/secret-menu/auto-brand/steps/ReviewSubmitStep';
import { initialAutoBrandData, type AutoBrandData } from 'src/secret-menu/auto-brand/types';

type AutoBrandDrawerProps = {
  opened: boolean;
  close: () => void;
  initialCompany?: Company | null;
};

const steps: DrawerFlowStep<AutoBrandData>[] = [
  {
    title: 'Select tenant',
    screens: [
      { component: SelectTenantStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' },
    ],
  },
  {
    title: 'Upload source',
    screens: [{ component: UploadDocumentStep, allowVerticalOverflow: true }],
  },
  {
    title: 'Manual correction',
    screens: [
      { component: ManualCorrectionStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' },
    ],
  },
  {
    title: 'Logos and page layout',
    screens: [{ component: UploadLogosStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' }],
  },
  {
    title: 'Title page',
    screens: [{ component: TitlePageStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' }],
  },
  {
    title: 'Save & submit',
    screens: [
      { component: ReviewSubmitStep, allowVerticalOverflow: true, maxContainerWidth: 'lg' },
    ],
  },
];

export default function AutoBrandDrawer({ opened, close, initialCompany }: AutoBrandDrawerProps) {
  const [data, setData] = useState<AutoBrandData>({
    ...initialAutoBrandData,
    company: initialCompany ?? null,
  });

  const companyId = data.company?.id ?? '';
  const { branding } = useExportBranding(companyId);

  useEffect(() => {
    if (!branding) return;
    setData(prev => ({
      ...prev,
      existingBranding: branding,
      config: prev.config ?? branding.config,
    }));
  }, [branding]);

  useEffect(() => {
    if (opened) {
      setData({ ...initialAutoBrandData, company: initialCompany ?? null });
    }
  }, [opened, initialCompany]);

  return (
    <DrawerFlow
      opened={opened}
      close={close}
      title="Auto brand"
      steps={steps}
      initialData={{ ...initialAutoBrandData, company: initialCompany ?? null }}
      data={data}
      setData={setData}
      drawerProps={{ closeOnEscape: false }}
    />
  );
}
